import { useEffect, useRef, useState } from 'react';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faCircle } from '@fortawesome/pro-regular-svg-icons/faCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { tests } from 'us-react-components/dist/constants/Tests';
import { SitterProfileStatus, SitterProfileStatusService } from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

interface ChecklistItem {
  id: string;
  label: string;
  complete?: boolean;
  path?: string;
  isOutbound?: boolean;
  popover?: { cta: string; link: string };
}

interface Quote {
  title: string;
  text: string;
  name: string;
  picUrl: string;
}

const SITTER_QUOTES: Quote[] = [
  {
    title: 'From the UrbanSitter Facebook Group',
    text: 'As head of household and her company my boss is constantly being pulled in many directions. The best part is, she still makes time for her kids. I never had a role model, until the day I met her. Is anyone else inspired by the mothers/fathers they work for?!',
    name: 'Krisztina G.',
    picUrl:
      'https://assets.urbansitter.com/sites/default/files/pictures/picture-medium-165363-fec38b6cccc126001bdf9a9bc7b9fd94.jpg',
  },
  {
    title: 'What to bring on babysitting jobs',
    text: "I have a 'babysitting bag' that I bring with me to most jobs. It's packed with essentials for any circumstance: my jacket, water bottle, hand sanitizer, sunscreen, and a snack. I also like to bring craft supplies for the kids so I can be prepared for any situation!",
    name: 'Sarah H.',
    picUrl:
      'https://assets.urbansitter.com/sites/default/files/pictures/picture-medium-10674-c9a9dfc79e84558a4f6467729901f466.jpg',
  },
  {
    title: 'Good communication with families',
    text: "I like to ask parents if they'd like a quick update while they're out. Sending a quick message to let them know that everything is going well has been part of what's helped me create lasting relationships with so many families.",
    name: 'Brittney L.',
    picUrl:
      'https://assets.urbansitter.com/sites/default/files/pictures/picture-medium-1737-b79b853cf6d899a299e6707c1e616632.jpg',
  },
  {
    title: 'From the UrbanSitter Facebook Group',
    text: 'Love UrbanSitter. Right when I needed extra cash for my trip, I was booked and have spending money now. :)',
    name: 'Jennifer A.',
    picUrl:
      'https://assets.urbansitter.com/sites/default/files/pictures/picture-medium-9345-8dd82a7dca0f3c83c16736e05e4f5bf7.jpg',
  },
  {
    title: 'From the UrbanSitter Facebook Group',
    text: 'Thank you to this wonderful community for all the advice, support, and reassurance. I had my first job this weekend and I really think it was due to the advice and good vibes I was given from all you wonderful people.',
    name: 'Madeline J.',
    picUrl:
      'https://assets.urbansitter.com/sites/default/files/pictures/picture-big-600121-5ccf49f5366c0bab2cf3afbb18669b30.jpg',
  },
  {
    title: 'What to do when you arrive on the job',
    text: "I always offer to wash my hands and take my shoes off when I arrive at jobs. It helps show parents that I'm ready to follow the house-rules and that I care about creating a safe space for the kids.",
    name: 'Niel V.',
    picUrl: 'https://assets.urbansitter.com/static/img/photos/nielv.webp',
  },
];

const FACEBOOK_GROUP_URL = 'https://www.facebook.com/groups/173689669398648/';

export const SuccessGuide = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const { stores } = useDependencies();
  const user = stores.user.user;
  const sitter = stores.sitter.sitter;

  const [status, setStatus] = useState<SitterProfileStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);
  const popoverContainerRef = useRef<HTMLDivElement | null>(null);

  // Pick a random quote once per mount (matches Angular $watch-once behavior)
  const [randomQuote] = useState<Quote>(
    () => SITTER_QUOTES[Math.floor(Math.random() * SITTER_QUOTES.length)],
  );

  useEffect(() => {
    document.title = 'UrbanSitter - Caregiver Success Guide';
  }, []);

  // Pull fresh sitter so acceptsCreditCards reflects current server state, not
  // stale localStorage. Without this, the "Add payout" step can render checked
  // for sitters who are no longer accepting credit cards.
  useEffect(() => {
    if (!user?.id) return;
    void (async (): Promise<void> => {
      try {
        await stores.sitter.refreshSitter();
      } catch (err) {
        console.warn('[SuccessGuide] sitter refresh failed', err);
      }
    })();
  }, [user?.id, stores.sitter]);

  useEffect(() => {
    const userId = user?.id;

    if (!userId) return;

    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const resp = await SitterProfileStatusService.get(userId);
        if (cancelled) return;
        setStatus(resp.data?.data ?? null);
      } catch (err) {
        console.warn('[SuccessGuide] status load failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return (): void => {
      cancelled = true;
    };
  }, [user?.id]);

  // Close popover on outside click (mirrors share-link-popover body click handler)
  useEffect(() => {
    if (!openPopoverId) return undefined;
    const handler = (e: MouseEvent): void => {
      if (
        popoverContainerRef.current &&
        !popoverContainerRef.current.contains(e.target as Node)
      ) {
        setOpenPopoverId(null);
      }
    };
    document.body.addEventListener('click', handler);
    return (): void => document.body.removeEventListener('click', handler);
  }, [openPopoverId]);

  const goTo = (item: ChecklistItem): void => {
    if (!item.path) return;
    if (item.isOutbound) {
      window.open(item.path);
    } else if (item.path.startsWith('/app')) {
      window.location.href = item.path;
    } else {
      navigate(item.path);
    }
  };

  const copyLink = async (link: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success(t('pages.successGuide.copiedToast'));
    } catch {
      // Clipboard API unavailable; the input remains selectable as fallback
    }
  };

  if (loading) {
    return (
      <div className='container py-8'>
        <p className='text-neutral-600'>{t('common.loading')}</p>
      </div>
    );
  }

  const isTester = stores.user.inTestGroup(tests.CAREGIVER_PAYWALL_STRIPE_PAYMENT);

  const backgroundCheckPurchased = status?.backgroundCheckPurchased;
  const backgroundCheckCleared = status?.backgroundCheckCleared;
  const bankAccount = status?.bankAccount;

  const leftList: ChecklistItem[] = [
    {
      id: 'buy-background-check',
      label: backgroundCheckPurchased?.label ?? t('pages.successGuide.buyBackgroundCheck'),
      complete: backgroundCheckPurchased?.complete,
      path: isTester ? '/caregiver/buy-background-check' : '/buy-background-check',
    },
    {
      id: 'finish-background-check',
      label: backgroundCheckCleared?.label ?? t('pages.successGuide.finishBackgroundCheck'),
      complete: backgroundCheckCleared?.complete,
      isOutbound: !!backgroundCheckCleared?.url,
      path: backgroundCheckCleared?.url || '/finish-background-check',
    },
  ];

  const rightList: ChecklistItem[] = [
    {
      id: 'add-avail-2-calendar',
      label: t('pages.successGuide.addAvailability'),
      path: '/sitter-calendar',
    },
    {
      id: 'add-payout-account',
      label: bankAccount?.label ?? t('pages.successGuide.addPayout'),
      // bankAccount.complete is true as soon as a payout method exists; it
      // doesn't reflect Branch credit-card setup, which is what this step
      // requires. Gate on sitter.acceptsCreditCards too.
      complete: !!bankAccount?.complete && !!sitter?.acceptsCreditCards,
      path: '/app/sitter/payout',
    },
    {
      id: 'add-video',
      label: t('pages.successGuide.addVideo'),
      complete: !!sitter?.vimeoId,
      path: '/sitter/video',
    },
    {
      id: 'improve-profile',
      label: t('pages.successGuide.improveProfile'),
      path: user?.id ? `/sitter/${user.id}` : undefined,
    },
    {
      id: 'get-recommendations-link',
      label: t('pages.successGuide.getRecommendations'),
      popover: {
        cta: t('pages.successGuide.copyCta'),
        link: user?.id
          ? `https://${window.location.host}/sitter/${user.id}/recommend`
          : '',
      },
    },
  ];

  const renderButton = (item: ChecklistItem): JSX.Element => {
    if (item.popover) {
      const isOpen = openPopoverId === item.id;
      return (
        <div
          ref={isOpen ? popoverContainerRef : undefined}
          className='relative'
        >
          <Button
            id={item.id}
            buttonType={ButtonType.secondary}
            onClick={(e): void => {
              e.stopPropagation();
              setOpenPopoverId(isOpen ? null : item.id);
            }}
            className='flex items-center justify-between w-full text-left px-4 py-3 rounded border border-neutral-300 bg-white hover:bg-neutral-50'
          >
            <span>{item.label}</span>
            <span className='text-primary font-semibold ml-2'>
              {item.popover.cta}
            </span>
          </Button>
          {isOpen && (
            <div className='absolute left-0 right-0 mt-2 p-4 rounded border border-neutral-300 bg-white shadow-lg z-10'>
              <div className='font-semibold mb-2'>
                {t('pages.successGuide.copyInstructions')}
              </div>
              <div className='flex gap-2'>
                <input
                  id='share-link-input'
                  aria-label='Share link'
                  type='text'
                  className='flex-1 border border-neutral-300 rounded px-2 py-1 text-sm'
                  value={item.popover.link}
                  readOnly
                  onFocus={(e): void => e.currentTarget.select()}
                />
                <Button
                  buttonType={ButtonType.primary}
                  onClick={(): Promise<void> => copyLink(item.popover!.link)}
                  className='px-3 py-1 rounded text-sm'
                >
                  {t('pages.successGuide.copyCta')}
                </Button>
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <Button
        id={item.id}
        buttonType={ButtonType.secondary}
        onClick={(): void => goTo(item)}
        disabled={item.complete || !item.path}
        className='flex items-center justify-between w-full text-left px-4 py-3 rounded border border-neutral-300 bg-white hover:bg-neutral-50 disabled:bg-neutral-50 disabled:cursor-default'
      >
        <span className={item.complete ? 'text-neutral-500' : ''}>
          {item.label}
        </span>
        <FontAwesomeIcon
          icon={item.complete ? faCheck : faCircle}
          className={item.complete ? 'text-primary' : 'text-neutral-300'}
        />
      </Button>
    );
  };

  return (
    <div className='container py-8'>
      <h1 className='text-2xl font-semibold mb-6'>
        {t('pages.successGuide.heading')}
      </h1>

      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
        <div className='lg:col-span-2 bg-white rounded-lg p-6 shadow-sm'>
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-6'>
            <section>
              <h2 className='text-lg font-semibold mb-4'>
                {t('pages.successGuide.approvalHeading')}
              </h2>
              <div className='space-y-3'>
                {leftList.map((item) => (
                  <div key={item.id}>{renderButton(item)}</div>
                ))}
              </div>
            </section>
            <section>
              <h2 className='text-lg font-semibold mb-4'>
                {t('pages.successGuide.profileHeading')}
              </h2>
              <div className='space-y-3'>
                {rightList.map((item) => (
                  <div key={item.id}>{renderButton(item)}</div>
                ))}
              </div>
            </section>
          </div>
        </div>

        <aside className='sitter-community bg-white rounded-lg p-6 shadow-sm'>
          <h3 className='font-semibold mb-4'>
            {t('pages.successGuide.community.title')}
          </h3>
          <h5 className='font-semibold mb-3'>{randomQuote.title}</h5>
          <div className='flex items-start gap-3 mb-2'>
            <img
              alt={randomQuote.name}
              src={randomQuote.picUrl}
              className='w-16 h-16 rounded-full object-cover flex-shrink-0'
            />
            <div className='text-center italic text-sm flex-1'>
              &quot;{randomQuote.text}&quot;
            </div>
          </div>
          <div className='text-right text-sm'>&ndash; {randomQuote.name}</div>
          <a
            id='join-facebook-group-btn'
            href={FACEBOOK_GROUP_URL}
            target='_blank'
            rel='noreferrer'
            className='block w-full mt-5 px-4 py-2 rounded border border-neutral-300 bg-white text-center font-semibold hover:bg-neutral-50'
          >
            {t('pages.successGuide.community.joinFacebook')}
          </a>
        </aside>
      </div>
    </div>
  );
});
